import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../hooks/locale';
import Table from '@amzn/awsui-components-react/polaris/table';
import Header from '@amzn/awsui-components-react/polaris/header';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import Button from '@amzn/awsui-components-react/polaris/button';
import ButtonDropdown from '@amzn/awsui-components-react/polaris/button-dropdown';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';
import Link from '@amzn/awsui-components-react/polaris/link';
import Box from '@amzn/awsui-components-react/polaris/box';
import { PropertyFilterProps } from '@amzn/awsui-components-react/polaris/property-filter';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  BulkLeaveWaitlistCommandOutput,
  Shift,
  WaitlistEntry,
  WaitlistStatus,
} from '@amzn/red-velvet-api';
import { useTimezone } from '../../hooks/timezone';
import { formatShiftDate, asTimeString } from '../../utils/dateFormatting';
import { ColumnMap, useTableColumns } from '../../hooks/tableColumns';
import { TablePropertyFilter } from '../attendance/wizard/tablePropertyFilter';
import { usePropertyFilterStrings } from '../../hooks/localization/propertyFilterStrings';
import { usePaginationStrings } from '../../hooks/localization/paginationStrings';
import { PeoplePreferences } from '../attendance/wizard/peoplePreferences';
import { buildWaitlistRows, WaitlistRow } from '../../utils/waitlistUtils';
import { RemoveWaitlistsModal } from './removeWaitlistsModal';

const WAITLIST_PAGE_SIZE = 100;
const NAMESPACE = 'coordinatorManageWaitlist';

const FILTER_PROPERTY_KEYS = [
  'shiftName',
  'volunteerName',
  'status',
  'addedDate',
  'alias',
] as const;

interface WaitlistTableProps {
  /** Displayable (ACTIVE / SIGNED_UP) waitlist entries for the event. */
  entries: WaitlistEntry[];
  shifts: Shift[];
  isLoading: boolean;
  /**
   * Called once a bulk-leave request returns (full or partial success), so the page can
   * surface the per-item result in a flashbar and refetch the waitlist.
   */
  onBulkRemoveComplete: (result: BulkLeaveWaitlistCommandOutput) => void;
}

export function WaitlistTable({
  entries,
  shifts,
  isLoading,
  onBulkRemoveComplete,
}: WaitlistTableProps) {
  const { t } = useTranslation(NAMESPACE);
  const locale = useLocale();
  const selectedTimezone = useTimezone();
  const paginationStrings = usePaginationStrings('pagination', [NAMESPACE]);
  const propertyFilterStrings = usePropertyFilterStrings('propertyFilter', [NAMESPACE]);

  const [pageSize, setPageSize] = useState(WAITLIST_PAGE_SIZE);
  const [selectedItems, setSelectedItems] = useState<WaitlistRow[]>([]);
  const [entriesToRemove, setEntriesToRemove] = useState<WaitlistEntry[]>([]);
  const [modalVisible, setModalVisible] = useState(false);

  const rows = useMemo(
    () =>
      buildWaitlistRows(entries, shifts, {
        statusLabel: (status) =>
          status === WaitlistStatus.SIGNED_UP ? t('status.signedUp') : t('status.onWaitlist'),
        addedDate: (createdTime) =>
          formatShiftDate(new Date(createdTime), selectedTimezone, locale),
        addedTime: (createdTime) => asTimeString(new Date(createdTime), selectedTimezone, locale),
      }),
    [entries, shifts, t, selectedTimezone, locale]
  );

  const filteringProperties: PropertyFilterProps.FilteringProperty[] = useMemo(
    () =>
      FILTER_PROPERTY_KEYS.map((key) => ({
        key,
        propertyLabel: t(`propertyFilter.properties.${key}`),
        groupValuesLabel: t(`propertyFilter.values.${key}`),
        operators: [':'],
        defaultOperator: ':',
      })),
    [t]
  );

  const columnMap: ColumnMap<WaitlistRow> = {
    position: (row) => String(row.positionInShift),
    shiftName: (row) => row.shiftName,
    volunteerName: (row) => row.volunteerName,
    status: (row) => (
      <StatusIndicator type={row.isActive ? 'in-progress' : 'success'}>
        {row.status}
      </StatusIndicator>
    ),
    addedDate: (row) => row.addedDate,
    addedTime: (row) => row.addedTime,
    alias: (row) => (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${row.alias}`}>
        {row.alias}
      </Link>
    ),
    actions: (row) =>
      row.isActive ? (
        <ButtonDropdown
          data-testid="waitlist-actions-dropdown"
          variant="icon"
          expandToViewport
          ariaLabel={t('table.actionsAriaLabel')}
          items={[{ id: 'leave', text: t('table.leaveWaitlist') }]}
          onItemClick={() => openRemoveModal([row.waitlistEntry])}
        />
      ) : (
        ''
      ),
  };

  const { columnDefinitions: baseColumnDefinitions, columnDisplay } = useTableColumns(
    columnMap,
    'table',
    [NAMESPACE]
  );

  const columnDefinitions = useMemo(() => {
    const sortableFieldMapping: Record<string, string> = {
      position: 'positionInShift',
      shiftName: 'shiftName',
      volunteerName: 'volunteerName',
      status: 'status',
      addedDate: 'addedTimestamp',
      addedTime: 'addedTimestamp',
      alias: 'alias',
    };
    return baseColumnDefinitions.map((col) => {
      const sortingField = sortableFieldMapping[col.id ?? ''];
      if (!sortingField) return col;
      return { ...col, sortingField };
    });
  }, [baseColumnDefinitions]);

  const { items, collectionProps, propertyFilterProps, paginationProps } = useCollection(rows, {
    filtering: {
      empty: (
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <b>{t('table.noneFound')}</b>
        </Box>
      ),
    },
    propertyFiltering: { filteringProperties },
    pagination: { pageSize },
    sorting: {},
  });

  const openRemoveModal = (toRemove: WaitlistEntry[]) => {
    setEntriesToRemove(toRemove);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEntriesToRemove([]);
  };

  return (
    <>
      <Table
        {...collectionProps}
        columnDefinitions={columnDefinitions}
        columnDisplay={columnDisplay}
        items={items}
        loading={isLoading}
        empty={
          <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
            <b>{t('table.noneFound')}</b>
          </Box>
        }
        trackBy={(row) => row.waitlistEntry.waitlistEntryId}
        selectionType="multi"
        selectedItems={selectedItems}
        isItemDisabled={(row) => !row.isActive}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        ariaLabels={{
          selectionGroupLabel: t('table.selectionGroupLabel'),
          allItemsSelectionLabel: () => t('table.selectAll'),
          itemSelectionLabel: (_data, row) => t('table.selectRow', { name: row.volunteerName }),
        }}
        header={
          <Header
            data-testid="waitlist-table-header"
            counter={`(${rows.length})`}
            actions={
              <Button
                disabled={selectedItems.length === 0}
                onClick={() => openRemoveModal(selectedItems.map((row) => row.waitlistEntry))}
              >
                {t('table.removeSelected')}
              </Button>
            }
          >
            {t('table.title')}
          </Header>
        }
        filter={
          <TablePropertyFilter
            {...propertyFilterProps}
            propertyFilterStrings={propertyFilterStrings}
            filteringPlaceholder={t('propertyFilter.filteringPlaceholder')}
          />
        }
        pagination={<Pagination {...paginationProps} ariaLabels={paginationStrings} />}
        preferences={
          <PeoplePreferences
            onConfirm={({ detail }) => setPageSize(detail.pageSize || WAITLIST_PAGE_SIZE)}
            preferences={{ pageSize }}
          />
        }
      />
      <RemoveWaitlistsModal
        visible={modalVisible}
        entries={entriesToRemove}
        onDismiss={closeModal}
        onComplete={(result) => {
          closeModal();
          setSelectedItems([]);
          onBulkRemoveComplete(result);
        }}
      />
    </>
  );
}
